/**
 * Rendering an instant, for a console whose whole point is an auditable record
 * of who did what when.
 *
 * These lived in `lib/jobs.ts` while the jobs screens were the only caller.
 * The audit trail formats the same kind of value and was doing it with a bare
 * `toLocaleString()`, so this is now shared rather than job-specific — the
 * name said "job" and the behaviour never did.
 */

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Whatever the runtime calls the viewer's own zone at this instant —
 * `EDT`, `GMT+5:30`, `UTC`. Asked of `Intl` in isolation, rather than folded
 * into one combined format with the date and time below, because
 * `Intl.DateTimeFormat`'s `hour12: false` is documented to print `24` for
 * midnight on ICU versions this app does not control; asking only for the
 * zone name sidesteps that entirely, leaving the clock arithmetic to `Date`'s
 * own local getters.
 */
function zoneAbbreviation(date: Date): string {
  const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(date);
  return parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
}

/**
 * A job timestamp as `YYYY-MM-DD HH:mm:ss <zone>`, in the viewer's own local
 * zone — never `toLocaleString()`, which prints no zone at all.
 *
 * This is the single worst information defect on this console: every
 * timestamp on every screen goes through this one function, and until now
 * none of them said which zone they were in. Two operators reading the same
 * row from different zones saw two different times with nothing to tell them
 * why — worst of all on a tool whose whole purpose is an auditable record of
 * who did what when. `Date`'s own local getters (`getFullYear`, `getHours`,
 * and so on) already report in the viewer's zone, which is the zone this is
 * for: an operator reads a timestamp against their own clock, not against
 * whatever zone the server happens to run in.
 *
 * Here rather than beside either screen because both of them read the same
 * three fields off the same records, and a list whose clock disagreed with the
 * detail view it links to would be a small, quiet way to make an operator
 * doubt what they are looking at mid-incident.
 */
export function formatTimestamp(iso: string): string {
  const parsed = new Date(iso);
  // The contract guarantees `date-time`, but rendering the raw string on a
  // parse failure beats throwing or silently showing "Invalid Date".
  if (Number.isNaN(parsed.getTime())) return iso;

  const date = `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
  const time = `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}:${pad(parsed.getSeconds())}`;
  const zone = zoneAbbreviation(parsed);

  // `Intl` failing to name the zone is not a reason to fail the whole
  // timestamp — the date and time above are still correct and worth showing.
  return zone === '' ? `${date} ${time}` : `${date} ${time} ${zone}`;
}

/**
 * The first 8 characters of a job id — enough to recognise a row without
 * reading the full 32-character opaque string, which used to cost this table
 * a quarter of its width for the one column an operator cannot read at a
 * glance anyway. The full id still exists: callers keep it in a `title`
 * attribute and in the link's destination, so nothing here changes what a job
 * is actually identified by, only how much of it sits in the cell.
 */
